import { parseCarriersData } from '@/features/shipment/services/shipment.services';
import { GetRatesData, TempPackageData, PackageItem } from '@/features/shipment/types';
import store from '@/features/common/store';
import { isPostalCodeValid } from '@/features/common/helpers/validationRules';
import { safeListedCountryCodes } from '@/features/common/raw';

function isPostalValid(countryCode: string, postalCode: string): boolean {
  const countryInfo = store.getters.getCountryFromCode(countryCode);
  if (!countryInfo) return false;

  return isPostalCodeValid(postalCode, countryInfo);
}

function isCityRequired(countryCode: string): boolean {
  return !!countryCode && !safeListedCountryCodes.includes(countryCode);
}

function isProvinceRequired(countryCode: string): boolean {
  return safeListedCountryCodes.includes(countryCode);
}

function hasMinChars(value: string | undefined, min: number): boolean {
  return !!value && value.trim().length >= min;
}

function hasValidPhone(phone: string | undefined): boolean {
  if (!phone) return false;
  const digits = phone.replace(/[^0-9]/gm, '');
  return digits.length >= 5;
}

/** Estimate flow: country + postal code + city (conditional) */
function isValidEstimateAddress(address: {
  countryCode?: string;
  postalCode?: string;
  city?: string;
}): boolean {
  if (!address?.countryCode || !address?.postalCode) return false;
  if (!isPostalValid(address.countryCode, address.postalCode)) return false;
  if (isCityRequired(address.countryCode) && !address.city?.trim()) return false;

  return true;
}

/** Shipment flow: all estimate checks + addr1, company, contact, phone, province */
function isValidShipmentAddress(address: {
  countryCode?: string;
  postalCode?: string;
  city?: string;
  addr1?: string;
  company?: string;
  contact?: string;
  phone?: string;
  province?: string;
}): boolean {
  if (!isValidEstimateAddress(address)) return false;
  if (!address.addr1?.trim()) return false;
  if (!hasMinChars(address.company, 2)) return false;
  if (!hasMinChars(address.contact, 2)) return false;
  if (!hasValidPhone(address.phone)) return false;
  if (isProvinceRequired(address.countryCode!) && !address.province?.trim()) return false;

  return true;
}

function hasValidPackages(packages: any[]): boolean {
  if (!packages?.length) return false;
  return packages.some((pkg) => pkg.weight > 0);
}

function isValidTempPackage(temp: TempPackageData): boolean {
  if (!temp) return false;
  if (!temp.packageType) return false;
  if (!temp.weightUnits) return false;

  // Envelope always has weight=1 Lbs
  if (temp.packageType === 'Envelope') return true;

  if (!temp.weight || Number(temp.weight) <= 0) return false;

  // If any dimension is set or packageType is Pallet, all dimensions are required
  if (temp.length || temp.width || temp.height || temp.packageType === 'Pallet') {
    if (!temp.length || Number(temp.length) <= 0) return false;
    if (!temp.width || Number(temp.width) <= 0) return false;
    if (!temp.height || Number(temp.height) <= 0) return false;
    if (!temp.dimUnits) return false;
  }

  return true;
}

function buildPackageFromTemp(temp: TempPackageData): PackageItem {
  let weight = Number(temp.weight);
  let { weightUnits } = temp;

  if (temp.packageType === 'Envelope') {
    weight = 1;
    weightUnits = 'Lbs';
  }

  return {
    packageType: temp.packageType,
    weight,
    weightUnits,
    length: Number(temp.length) || 0,
    width: Number(temp.width) || 0,
    height: Number(temp.height) || 0,
    dimUnits: temp.dimUnits,
    insurance: Number(temp.insurance) || 0,
    isAdditionalHandling: temp.isAdditionalHandling,
    description: temp.description,
    signatureOptions: temp.signatureOptions || 'None',
  };
}

function hasValidPackagesOrTemp(
  packages: any[],
  temporaryPackage?: TempPackageData,
  isAutoAdd?: boolean,
): { valid: boolean; packagesForPrefetch: PackageItem[] | null } {
  if (packages?.length && packages.some((pkg) => pkg.weight > 0)) {
    return { valid: true, packagesForPrefetch: null };
  }

  if (isAutoAdd && temporaryPackage && isValidTempPackage(temporaryPackage)) {
    return { valid: true, packagesForPrefetch: [buildPackageFromTemp(temporaryPackage)] };
  }

  return { valid: false, packagesForPrefetch: null };
}

export function isReadyForEstimatePrefetch(data: {
  fromAddress: { countryCode?: string; postalCode?: string; city?: string };
  toAddress: { countryCode?: string; postalCode?: string; city?: string };
  packages: any[];
}): boolean {
  return isValidEstimateAddress(data.fromAddress)
    && isValidEstimateAddress(data.toAddress)
    && hasValidPackages(data.packages);
}

export function isReadyForShipmentPrefetch(data: {
  fromAddress: {
    countryCode?: string; postalCode?: string; city?: string;
    addr1?: string; company?: string; contact?: string; phone?: string; province?: string;
  };
  toAddress: {
    countryCode?: string; postalCode?: string; city?: string;
    addr1?: string; company?: string; contact?: string; phone?: string; province?: string;
  };
  packages: any[];
  temporaryPackage?: TempPackageData;
  isAutoAddPackage?: boolean;
}): { ready: boolean; packagesForPrefetch: PackageItem[] | null } {
  if (!isValidShipmentAddress(data.fromAddress)) return { ready: false, packagesForPrefetch: null };
  if (!isValidShipmentAddress(data.toAddress)) return { ready: false, packagesForPrefetch: null };

  const result = hasValidPackagesOrTemp(
    data.packages, data.temporaryPackage, data.isAutoAddPackage,
  );
  return { ready: result.valid, packagesForPrefetch: result.packagesForPrefetch };
}

function buildPrefetchKey(data: GetRatesData): string {
  try {
    const packages = (data.packages || []).map((pkg) => ({
      t: pkg.packageType,
      w: Number(pkg.weight) || 0,
      wu: pkg.weightUnits,
      l: Number(pkg.length) || 0,
      wi: Number(pkg.width) || 0,
      h: Number(pkg.height) || 0,
      du: pkg.dimUnits || '',
      i: Number(pkg.insurance) || 0,
      ah: !!pkg.isAdditionalHandling,
    }));

    return JSON.stringify({
      fc: data.fromAddress?.countryCode,
      fp: data.fromAddress?.postalCode,
      fci: data.fromAddress?.city || '',
      tc: data.toAddress?.countryCode,
      tp: data.toAddress?.postalCode,
      tci: data.toAddress?.city || '',
      pkg: packages,
      doc: !!data.documentsOnly,
    });
  } catch {
    return '';
  }
}

export type Prefetcher = {
  (data: GetRatesData): void;
  cancel: () => void;
};

// Module-level key storage so dedup survives component re-mounts
const lastPrefetchKeys: Record<string, string> = {};

export function createPrefetcher(caller: 'Rates' | 'CreateShipment', delayMs = 1500): Prefetcher {
  let timeout: ReturnType<typeof setTimeout>;

  function prefetch(data: GetRatesData): void {
    const prefetchKey = buildPrefetchKey(data);

    if (prefetchKey && prefetchKey === lastPrefetchKeys[caller]) return;
    lastPrefetchKeys[caller] = prefetchKey;

    clearTimeout(timeout);
    timeout = setTimeout(async () => {
      try {
        await parseCarriersData(data, caller);
      } catch {
        // Silently ignore — this is a prefetch
      }
    }, delayMs);
  }

  prefetch.cancel = () => {
    clearTimeout(timeout);
    lastPrefetchKeys[caller] = '';
  };

  return prefetch;
}
